import { useState, useEffect, useRef, useCallback } from "react";
import { NavLink } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/context/AuthContext";
import { useShows } from "@/context/ShowContext";
import { premiumApi } from "@/api/premium.api";

interface NavItem { to: string; label: string; icon: string; }

const ICONS = {
  dashboard: "M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-4 0h4",
  setup: "M12 6v6m0 0v6m0-6h6m-6 0H6",
  shows: "M7 4v16M17 4v16M3 8h4m10 0h4M3 12h18M3 16h4m10 0h4M4 20h16a1 1 0 001-1V5a1 1 0 00-1-1H4a1 1 0 00-1 1v14a1 1 0 001 1z",
  profile: "M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z",
  sales: "M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z",
  density: "M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z",
  financials: "M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
  reports: "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z",
  recs: "M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z",
  goals: "M13 10V3L4 14h7v7l9-11h-7z",
  marketing: "M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z",
  missions: "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4",
  readiness: "M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z",
  leaderboard: "M16 8v8m-4-5v5m-4-2v2m-2 4h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z",
  live: "M13 10V3L4 14h7v7l9-11h-7z",
  tweaks: "M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z",
  extra: "M12 9v6m3-3H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z",
  win: "M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z",
  lock: "M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z",
  users: "M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z",
  adminShows: "M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10",
  feeds: "M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15",
  log: "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2",
  templates: "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z",
  academy: "M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14zm-4 6v-7.5l4-2.222",
  badges: "M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z",
  reference: "M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z",
  assets: "M20.25 7.5l-.625 10.632a2.25 2.25 0 01-2.247 2.118H6.622a2.25 2.25 0 01-2.247-2.118L3.75 7.5M10 11.25h4M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z",
};

const SECTIONS = {
  setup: { label: "SETUP", items: [
    { to: "/new-show-setup", label: "Setup", icon: ICONS.setup },
    { to: "/my-shows", label: "Shows", icon: ICONS.shows },
    { to: "/profile", label: "Profile", icon: ICONS.profile },
  ]},
  preseason: { label: "PRE-SEASON", items: [
    { to: "/upcoming-shows", label: "Upcoming Shows", icon: ICONS.shows },
    { to: "/goals", label: "Goals", icon: ICONS.goals },
    // { to: "/marketing", label: "Marketing", icon: ICONS.marketing },
    { to: "/missions", label: "Missions", icon: ICONS.missions },
    { to: "/badges", label: "Badges", icon: ICONS.badges },
    { to: "/reports/readiness", label: "Readiness", icon: ICONS.readiness },
    { to: "/leaderboard", label: "Leaderboard", icon: ICONS.leaderboard },
    { to: "/academy", label: "Learning Academy", icon: ICONS.academy },
    { to: "/assets", label: "Asset Library", icon: ICONS.assets },
    { to: "/reference", label: "Reference", icon: ICONS.reference },
  ]},
  inseason: { label: "IN-SEASON", items: [
    { to: "/live-dashboard", label: "Live Dashboard", icon: ICONS.live },
    { to: "/leaderboard", label: "Leaderboard", icon: ICONS.leaderboard },
    { to: "/badges", label: "Badges", icon: ICONS.badges },
    { to: "/daily-tweaks", label: "Daily Tweaks", icon: ICONS.tweaks },
    { to: "/extra-sales", label: "Extra Sales", icon: ICONS.extra },
    { to: "/win-tracker", label: "Win Tracker", icon: ICONS.win },
    { to: "/academy", label: "Learning Academy", icon: ICONS.academy },
    { to: "/assets", label: "Asset Library", icon: ICONS.assets },
    { to: "/reference", label: "Reference", icon: ICONS.reference },
  ]},
  complete: { label: "SEASON COMPLETE", items: [
    { to: "/reports/sales", label: "Sales", icon: ICONS.sales },
    { to: "/reports/density", label: "Density", icon: ICONS.density },
    { to: "/reports/financials", label: "Financials", icon: ICONS.financials },
    { to: "/reports/season", label: "Reports", icon: ICONS.reports },
    { to: "/reports/recommendations", label: "Recs", icon: ICONS.recs },
    { to: "/badges", label: "Badges", icon: ICONS.badges },
    { to: "/academy", label: "Learning Academy", icon: ICONS.academy },
    { to: "/assets", label: "Asset Library", icon: ICONS.assets },
    { to: "/reference", label: "Reference", icon: ICONS.reference },
  ]},
};

type ShowState = "PS" | "IS" | "CS";

function getShowState(firstPerf: string | null, lastPerf: string | null): ShowState {
  if (!firstPerf) return "PS";
  const today = new Date().toISOString().slice(0, 10);
  if (lastPerf && lastPerf < today) return "CS";
  if (firstPerf > today) return "PS";
  return "IS";
}

interface SectionConfig { key: string; label: string; items: NavItem[]; defaultOpen: boolean; locked: boolean; }

function getSections(state: ShowState): SectionConfig[] {
  if (state === "CS") return [
    { key: "setup", ...SECTIONS.setup, defaultOpen: true, locked: false },
    { key: "complete", ...SECTIONS.complete, defaultOpen: true, locked: false },
    { key: "preseason", ...SECTIONS.preseason, defaultOpen: false, locked: true },
    { key: "inseason", ...SECTIONS.inseason, defaultOpen: false, locked: true },
  ];
  if (state === "PS") return [
    { key: "setup", ...SECTIONS.setup, defaultOpen: true, locked: false },
    { key: "preseason", ...SECTIONS.preseason, defaultOpen: true, locked: false },
  ];
  return [
    { key: "setup", ...SECTIONS.setup, defaultOpen: false, locked: false },
    { key: "preseason", ...SECTIONS.preseason, defaultOpen: false, locked: true },
    { key: "inseason", ...SECTIONS.inseason, defaultOpen: true, locked: true },
    { key: "complete", ...SECTIONS.complete, defaultOpen: false, locked: true },
  ];
}

const adminItems: NavItem[] = [
  { to: "/admin/analytics", label: "Analytics", icon: ICONS.density },
  { to: "/admin/users", label: "Users", icon: ICONS.users },
  { to: "/admin/shows", label: "Shows", icon: ICONS.adminShows },
  { to: "/admin/feeds", label: "Feeds", icon: ICONS.feeds },
  { to: "/admin/festivals", label: "Festivals", icon: ICONS.goals },
  { to: "/admin/gamification", label: "Gamification", icon: ICONS.win },
  { to: "/admin/activity", label: "Log", icon: ICONS.log },
  { to: "/admin/traffic", label: "Traffic", icon: ICONS.density },
  { to: "/admin/templates", label: "Templates", icon: ICONS.templates },
  { to: "/admin/assets", label: "Asset List", icon: ICONS.assets },
];

function Icon({ d }: { d: string }) {
  return (
    <svg className="w-[20px] h-[20px] shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d={d} />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg className="w-[10px] h-[10px] shrink-0 text-[#808297]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d={ICONS.lock} />
    </svg>
  );
}

function AnimatedSection({ isOpen, children }: { isOpen: boolean; children: React.ReactNode }) {
  const contentRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number | undefined>(isOpen ? undefined : 0);

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    if (isOpen) {
      setHeight(el.scrollHeight);
      const t = setTimeout(() => setHeight(undefined), 300);
      return () => clearTimeout(t);
    } else {
      setHeight(el.scrollHeight);
      requestAnimationFrame(() => setHeight(0));
    }
  }, [isOpen]);

  return (
    <div
      style={{ height: height !== undefined ? `${height}px` : "auto", overflow: "hidden", transition: "height 300ms cubic-bezier(0.34, 1.56, 0.64, 1)" }}
    >
      <div ref={contentRef}>{children}</div>
    </div>
  );
}

function SectionHeading({ label, locked, hasPremium, isOpen, onToggle, sidebarExpanded }: {
  label: string; locked: boolean; hasPremium: boolean; isOpen: boolean; onToggle: () => void; sidebarExpanded: boolean;
}) {
  const showLock = locked && !hasPremium;
  const hoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleMouseEnter = () => {
    onToggle();
  };
  const handleMouseLeave = () => {
    if (hoverTimer.current) { clearTimeout(hoverTimer.current); hoverTimer.current = null; }
  };

  return (
    <>
      <div className="h-px ml-[9px]" style={{ background: "#4e4f6d", width: "20px" }} />
      <button
        onClick={onToggle}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className="flex items-center pl-[16px] pr-2 w-full text-left overflow-hidden"
        style={{ height: "24px", paddingTop: "4px" }}
      >
        <span
          className="shrink-0 transition-all duration-250 ease-in-out"
          style={{
            fontSize: "7px",
            color: isOpen ? "#808297" : "#636273",
            marginRight: sidebarExpanded ? "6px" : "0",
            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
            display: "inline-block",
            transition: "transform 300ms cubic-bezier(0.34, 1.56, 0.64, 1), margin-right 200ms ease-out, color 200ms",
          }}
        >
          &#9660;
        </span>
        <span
          className="whitespace-nowrap font-bold tracking-widest"
          style={{
            fontSize: "9px",
            color: "#636273",
            opacity: sidebarExpanded ? 1 : 0,
            transition: "opacity 150ms ease-in-out",
            transitionDelay: sidebarExpanded ? "100ms" : "0ms",
          }}
        >
          {label}
        </span>
        {showLock && sidebarExpanded && <LockIcon />}
      </button>
    </>
  );
}

interface SidebarProps { isOpen: boolean; onClose: () => void; expanded: boolean; }

export function Sidebar({ isOpen, onClose, expanded }: SidebarProps) {
  const { user, logout } = useAuth();
  const { selectedShow, selectedShowId } = useShows();
  const isAdmin = user?.role === "admin";
  const initials = (user?.displayName || user?.username || "?").charAt(0).toUpperCase();

  const showState = getShowState(selectedShow?.first_perf_date ?? null, selectedShow?.last_perf_date ?? null);
  const sections = getSections(showState);

  const { data: premiumStatus } = useQuery({
    queryKey: ["premium-status", selectedShowId],
    queryFn: () => premiumApi.getStatus(selectedShowId!),
    enabled: !!selectedShowId,
    staleTime: 60_000,
  });
  const hasPremium = !!premiumStatus?.is_premium;

  const [openSection, setOpenSection] = useState<string | null>(
    () => sections.find(s => s.defaultOpen)?.key ?? null
  );

  // Reset to the default-open section when the show state changes —
  // render-adjust instead of an effect (no cascading re-render)
  const [prevShowState, setPrevShowState] = useState(showState);
  if (prevShowState !== showState) {
    setPrevShowState(showState);
    setOpenSection(sections.find(s => s.defaultOpen)?.key ?? null);
  }

  const toggleSection = useCallback((key: string) => {
    setOpenSection(prev => prev === key ? null : key);
  }, []);

  const desktopLink = ({ isActive }: { isActive: boolean }) =>
    `flex items-center gap-2.5 h-9 rounded-md transition-colors duration-150 pl-[9px] ${
      isActive ? "text-[#b87800]" : "text-[#808297] hover:text-[#b87800]"
    }`;

  const fadedLink = ({ isActive }: { isActive: boolean }) =>
    `flex items-center gap-2.5 h-9 rounded-md transition-colors duration-150 pl-[9px] opacity-35 ${
      isActive ? "text-[#b87800]" : "text-[#808297]"
    }`;

  const mobileLink = ({ isActive }: { isActive: boolean }) =>
    `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-all ${
      isActive ? "bg-[#b87800]/15 text-[#b87800]" : "text-[#9695a4] hover:text-[#fafafc] hover:bg-white/[0.04]"
    }`;

  const fadedMobileLink = ({ isActive }: { isActive: boolean }) =>
    `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-all opacity-35 ${
      isActive ? "bg-[#b87800]/15 text-[#b87800]" : "text-[#9695a4]"
    }`;

  const isSectionAccessible = (section: SectionConfig) => {
    if (!section.locked) return true;
    return hasPremium;
  };

  const renderDesktopSection = (section: SectionConfig) => {
    const sectionOpen = openSection === section.key;
    const faded = !isSectionAccessible(section);
    const linkClass = faded ? fadedLink : desktopLink;

    return (
      <div key={section.key}>
        <SectionHeading
          label={section.label} locked={section.locked} hasPremium={hasPremium}
          isOpen={sectionOpen} onToggle={() => toggleSection(section.key)} sidebarExpanded={expanded}
        />
        <AnimatedSection isOpen={sectionOpen}>
          {section.items.map(item => (
            <NavLink key={item.to} to={item.to} className={linkClass} title={!expanded ? item.label : undefined}>
              <Icon d={item.icon} />
              {expanded && <span className="text-xs font-medium whitespace-nowrap">{item.label}</span>}
            </NavLink>
          ))}
        </AnimatedSection>
      </div>
    );
  };

  const renderMobileSection = (section: SectionConfig) => {
    const sectionOpen = openSection === section.key;
    const faded = !isSectionAccessible(section);
    const linkClass = faded ? fadedMobileLink : mobileLink;
    const showLock = faded;

    return (
      <div key={section.key}>
        <div className="h-px mx-2 my-1" style={{ background: "#4e4f6d" }} />
        <button
          onClick={() => toggleSection(section.key)}
          onMouseEnter={() => toggleSection(section.key)}
          className="flex items-center gap-1.5 px-3 py-1.5 w-full text-left"
        >
          <span style={{ fontSize: "7px", color: sectionOpen ? "#808297" : "#636273", transform: sectionOpen ? "rotate(180deg)" : "rotate(0deg)", display: "inline-block", transition: "transform 300ms cubic-bezier(0.34, 1.56, 0.64, 1)" }}>&#9660;</span>
          <span className="text-[#636273] font-bold tracking-widest" style={{ fontSize: "9px" }}>{section.label}</span>
          {showLock && <LockIcon />}
        </button>
        <AnimatedSection isOpen={sectionOpen}>
          <div className="space-y-0.5">
            {section.items.map(item => (
              <NavLink key={item.to} to={item.to} className={linkClass} onClick={onClose}>
                <Icon d={item.icon} /><span>{item.label}</span>
              </NavLink>
            ))}
          </div>
        </AnimatedSection>
      </div>
    );
  };

  const adminOpen = openSection === "admin";

  return (
    <>
      {isOpen && <div className="fixed inset-0 bg-black/50 z-40 md:hidden" onClick={onClose} />}

      {/* Desktop nav */}
      <nav className="hidden md:flex flex-1 flex-col pt-2 pb-3 overflow-y-auto overflow-x-hidden" style={{ gap: "4px" }}>
        <NavLink to="/dashboard" className={desktopLink} title={!expanded ? "Dashboard" : undefined}>
          <Icon d={ICONS.dashboard} />
          {expanded && <span className="text-xs font-medium whitespace-nowrap">Dashboard</span>}
        </NavLink>

        {sections.map(renderDesktopSection)}

        {isAdmin && (
          <div>
            <SectionHeading
              label="ADMIN" locked={false} hasPremium={true}
              isOpen={adminOpen} onToggle={() => toggleSection("admin")} sidebarExpanded={expanded}
            />
            <AnimatedSection isOpen={adminOpen}>
              {adminItems.map(item => (
                <NavLink key={item.to} to={item.to} className={desktopLink} title={!expanded ? item.label : undefined}>
                  <Icon d={item.icon} />
                  {expanded && <span className="text-xs font-medium whitespace-nowrap">{item.label}</span>}
                </NavLink>
              ))}
            </AnimatedSection>
          </div>
        )}

        <div className="flex-1" />
        <div className="flex items-center gap-2 pl-[5px] py-3">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#b87800] to-[#b87800] flex items-center justify-center text-[10px] font-bold text-white shrink-0">{initials}</div>
          {expanded && <span className="text-[10px] text-[#9695a4] truncate whitespace-nowrap">{user?.displayName || user?.username}</span>}
        </div>
        <button onClick={logout} className="text-[#808297] hover:text-[#cb505f] transition-colors flex items-center gap-2 pl-[12px]" title="Logout">
          <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3 0l3-3m0 0l-3-3m3 3H9" /></svg>
          {expanded && <span className="text-xs">Logout</span>}
        </button>
      </nav>

      {/* Mobile full sidebar */}
      <aside className={`fixed top-0 left-0 z-50 h-full w-60 transform transition-transform duration-200 md:hidden ${isOpen ? "translate-x-0" : "-translate-x-full"}`}
        style={{ backgroundColor: "#313949", backgroundImage: "url('/noise.png')", backgroundRepeat: "repeat", boxShadow: "0 0 10px 10px #191c2a" }}>
        <div className="flex flex-col h-full">
          <div className="p-4"><h1 className="text-base font-bold text-gradient">SellMoreTickets</h1></div>
          <nav className="flex-1 px-2 overflow-y-auto" style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <NavLink to="/dashboard" className={mobileLink} onClick={onClose}>
              <Icon d={ICONS.dashboard} /><span>Dashboard</span>
            </NavLink>
            {sections.map(renderMobileSection)}
            {isAdmin && (
              <>
                <div className="h-px mx-2 my-1" style={{ background: "#4e4f6d" }} />
                <button
                  onClick={() => toggleSection("admin")}
                  className="flex items-center gap-1.5 px-3 py-1.5 w-full text-left"
                >
                  <span style={{ fontSize: "7px", color: adminOpen ? "#808297" : "#636273", transform: adminOpen ? "rotate(180deg)" : "rotate(0deg)", display: "inline-block", transition: "transform 300ms cubic-bezier(0.34, 1.56, 0.64, 1)" }}>&#9660;</span>
                  <span className="text-[#636273] font-bold tracking-widest" style={{ fontSize: "9px" }}>ADMIN</span>
                </button>
                <AnimatedSection isOpen={adminOpen}>
                  {adminItems.map(item => (
                    <NavLink key={item.to} to={item.to} className={mobileLink} onClick={onClose}>
                      <Icon d={item.icon} /><span>{item.label}</span>
                    </NavLink>
                  ))}
                </AnimatedSection>
              </>
            )}
          </nav>
          <div className="p-3 flex items-center gap-2">
            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#b87800] to-[#b87800] flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0">{initials}</div>
            <div className="min-w-0 flex-1"><p className="text-xs font-medium text-[#fafafc] truncate">{user?.displayName || user?.username}</p></div>
            <button onClick={logout} className="text-[#808297] hover:text-[#cb505f] text-xs p-1"><svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3 0l3-3m0 0l-3-3m3 3H9" /></svg></button>
          </div>
        </div>
      </aside>
    </>
  );
}
